import { Controller } from "stimulus"
import { calculateWellbyImpact } from "../utils/wellby_impact_calculator"

// Generic, reusable WELLBY-style "estimated impact" widget 
export default class extends Controller {
  static targets = [
    "wellbys", "depressionYears", "breakdown", "timeframeWrapper", "ageWrapper", "age",
    "wellbysLabel", "depressionYearsLabel", "wellbysDelta", "depressionYearsDelta"
  ]

  static values = {
    config: Object,
    amountCents: Number,
    variant: String,
    boostBaselineCents: Number
  }

  initialize() {
    this.timeframe = "year"
    this.render()
  }

  amountCentsValueChanged() {
    this.render()
  }

  boostBaselineCentsValueChanged() {
    this.render()
  }

  // Lets an external controller update the allocations fed into this calculator after connect.
  configValueChanged() {
    this.render()
  }

  // The freq toggle can flip the donor between one-time/recurrent client-side without a reload --
  // the Per month/year/lifetime pills only make sense for a recurring gift.
  variantValueChanged() {
    if (this.hasTimeframeWrapperTarget) this.timeframeWrapperTarget.classList.toggle("is-hidden", this.variantValue !== "recurrent")
    this.render()
  }

  timeframeChanged(event) {
    this.timeframe = event.target.value
    if (this.hasAgeWrapperTarget) this.ageWrapperTarget.classList.toggle("is-hidden", this.timeframe !== "lifetime")
    this.render()
  }

  ageEntered() {
    this.render()
  }

  // 1x for "month" (the base figure), 12x for "year", 0x if no/too-old age entered. 
  // One-time gifts have no period, so the timeframe pills are hidden and this always returns 1x.
  get multiplier() {
    if (this.variantValue !== "recurrent") return 1
    if (this.timeframe === "year") return 12
    if (this.timeframe === "lifetime") {
      const age = this.hasAgeTarget ? parseInt(this.ageTarget.value, 10) : NaN
      const endAge = this.configValue.lifetime_end_age
      if (!age || age >= endAge) return 0
      return (endAge - age) * 12
    }
    return 1
  }

  get monthlyDepressionYears() {
    const unitsPerUsd = this.unitsPerUsd
    if (unitsPerUsd === null) return 0

    const { wellbys } = calculateWellbyImpact({ amountCents: this.amountCentsValue, allocations: this.configValue.allocations, isZeroDecimal: this.isZeroDecimalCurrency, unitsPerUsd })
    return wellbys / this.configValue.depression_years_per_wellby
  }
  // donation_flow_shared_controller.js stamps both of these onto its own amountCurrencySymbol element, this widget has no currency selector of its own.
  get symbolElement() {
    return this.element.closest("form")?.querySelector('[data-donation-flow-shared-target="amountCurrencySymbol"]')
  }

  // Stripe's zero-decimal currencies (JPY, KRW, VND, ...) have no minor unit.
  get isZeroDecimalCurrency() {
    return this.symbolElement?.dataset.zeroDecimal === "true"
  }

  // cost_per_wellby/cost_per_outcome in config are USD-denominated,  this converts the donor's own-currency amount to its USD equivalent before that math.
  get unitsPerUsd() {
    const value = this.symbolElement?.dataset.unitsPerUsd
    return value ? parseFloat(value) : null
  }

  // "WELLBYs generated" / "Years of Depression Averted (YoDAs)" gain a period suffix matching the selected Per month / Per year / Lifetime tab — one-time donations have no period, so no suffix.
  get periodSuffix() {
    if (this.variantValue !== "recurrent") return ""
    if (this.timeframe === "year") return " per year"
    if (this.timeframe === "lifetime") return " over your lifetime"
    return " per month"
  }

  syncLabels() {
    const suffix = this.periodSuffix
    if (this.hasWellbysLabelTarget) this.wellbysLabelTarget.textContent = `WELLBYs generated${suffix}`
    if (this.hasDepressionYearsLabelTarget) this.depressionYearsLabelTarget.textContent = `Years of Depression Averted (YoDAs)${suffix}`
  }

  render() {
    this.syncLabels()

    const unitsPerUsd = this.unitsPerUsd
    if (unitsPerUsd === null) {
      this.renderEmpty("Impact estimate not available for this currency yet.")
      return
    }

    const amountCents = this.amountCentsValue * this.multiplier
    const { wellbys, breakdown } = calculateWellbyImpact({ amountCents, allocations: this.configValue.allocations, isZeroDecimal: this.isZeroDecimalCurrency, unitsPerUsd })

    if (wellbys <= 0) {
      this.renderEmpty(this.configValue.empty_hint)
      return
    }

    this.wellbysTarget.textContent = this.formatNumber(wellbys)
    // depression_years_per_wellby converts wellbys into HLI's secondary headline metric.
    this.depressionYearsTarget.textContent = this.formatNumber(wellbys / this.configValue.depression_years_per_wellby)

    // Same allocations both times, so baseline.breakdown lines up with breakdown index-for-index.
    const baseline = this.boostBaselineImpact
    this.breakdownTarget.innerHTML = breakdown.map((allocation, index) => {
      const outcomesDelta = allocation.outcomes - (baseline?.breakdown[index]?.outcomes ?? 0)
      const deltaHtml = baseline && outcomesDelta > 0.005
        ? `<span class="donation-flow__impact-bk-delta">+${this.formatNumber(outcomesDelta)}</span>`
        : "<span></span>"
      return `
        <div class="donation-flow__impact-bk-row">
          <span class="donation-flow__impact-bk-name">${allocation.name}</span>
          ${deltaHtml}
          <strong class="donation-flow__impact-bk-value">${this.formatNumber(allocation.outcomes)}</strong>
          <span class="donation-flow__impact-bk-label">${allocation.outcomeLabel}</span>
        </div>
      `
    }).join("")

    this.renderHeadlineDelta(wellbys, baseline?.wellbys)
  }

  // The impact math re-run at the pre-boost amount. Every "+X" reads consistently with the numbers it sits next to.
  get boostBaselineImpact() {
    const hasBoost = this.boostBaselineCentsValue > 0 && this.amountCentsValue > this.boostBaselineCentsValue
    if (!hasBoost) return null

    const baselineAmountCents = this.boostBaselineCentsValue * this.multiplier
    return calculateWellbyImpact({ amountCents: baselineAmountCents, allocations: this.configValue.allocations, isZeroDecimal: this.isZeroDecimalCurrency, unitsPerUsd: this.unitsPerUsd })
  }

  renderHeadlineDelta(currentWellbys, baselineWellbys) {
    const deltaWellbys = baselineWellbys != null ? currentWellbys - baselineWellbys : 0
    if (deltaWellbys > 0.005) {
      this.showDelta(deltaWellbys, deltaWellbys / this.configValue.depression_years_per_wellby)
    } else {
      this.hideDelta()
    }
  }

  formatNumber(value) {
    return value >= 100 ? Math.round(value).toLocaleString() : value.toFixed(1)
  }

  // Driven externally by charity-split's "Boost your impact" slider. The marginal gain from bumping the donation above its original amount, shown next to the WELLBYs/years numbers.
  showDelta(wellbysDelta, depressionYearsDelta) {
    if (this.hasWellbysDeltaTarget) {
      this.wellbysDeltaTarget.textContent = `+${this.formatNumber(wellbysDelta)}`
      this.wellbysDeltaTarget.classList.remove("is-hidden")
    }
    if (this.hasDepressionYearsDeltaTarget) {
      this.depressionYearsDeltaTarget.textContent = `+${this.formatNumber(depressionYearsDelta)}`
      this.depressionYearsDeltaTarget.classList.remove("is-hidden")
    }
  }

  hideDelta() {
    if (this.hasWellbysDeltaTarget) this.wellbysDeltaTarget.classList.add("is-hidden")
    if (this.hasDepressionYearsDeltaTarget) this.depressionYearsDeltaTarget.classList.add("is-hidden")
  }

  // Shared by "no amount entered yet" (wellbys <= 0) and "conversion rate unknown" -- same blank state, different explanation.
  renderEmpty(hint) {
    this.wellbysTarget.textContent = "—"
    this.depressionYearsTarget.textContent = "—"
    this.breakdownTarget.innerHTML = hint ? `<p class="donation-flow__impact-hint">${hint}</p>` : ""
    this.hideDelta()
  }
}
